import React, { useState } from "react";

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import { useWarningSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";

type Priority = "High" | "Medium" | "Low";

const MAX_TAGS = 3;
const MAX_TAG_LENGTH = 24;
const MAX_NOTES_LENGTH = 1000;

type PriorityList = { label: string; value: Priority }[];

const PRIORITY_LIST: PriorityList = [
  { label: "High", value: "High" },
  { label: "Medium", value: "Medium" },
  { label: "Low", value: "Low" },
];

export type InsightEnhancementData = {
  priority: Priority;
  tags: string[];
  expertNote: string;
};

type Props = {
  existingPriority: string | null;
  existingTags: string[] | null;
  existingExpertNote: string | null;
  open: boolean;
  onClose: () => void;
  onSave: (enhancementData: InsightEnhancementData) => void;
};

const EnhanceInsightModal: React.FC<Props> = ({
  existingPriority,
  existingTags,
  existingExpertNote,
  open,
  onClose,
  onSave,
}) => {
  const warningSnackbar = useWarningSnackbar();

  const [priority, setPriority] = useState<Priority | "">((existingPriority as Priority) || "");
  const [tags, setTags] = useState<string[]>(existingTags || []);
  const [tagInput, setTagInput] = useState("");
  const [notes, setNotes] = useState(existingExpertNote || "");

  const resetState = () => {
    setPriority("");
    setTagInput("");
    setTags([]);
    setNotes("");
  };

  const addNewTag = () => {
    if (tagInput.trim() === "") {
      setTagInput("");
      return;
    }

    if (tags.length === MAX_TAGS) {
      warningSnackbar(`You can only add up to ${MAX_TAGS} tags.`);
      setTagInput("");
      return;
    }

    if (tags.some((tag) => tag.toLowerCase() === tagInput.trim().toLowerCase())) {
      warningSnackbar("Tag already exists.");
      setTagInput("");
      return;
    }

    setTags([...tags, tagInput.trim()]);
    setTagInput("");
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value.length > MAX_TAG_LENGTH) {
      warningSnackbar(`Tag length is limited to ${MAX_TAG_LENGTH} characters.`);
      return;
    }
    setTagInput(value);
  };
  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      addNewTag();
    }
  };

  const handleTagInputBlur = () => {
    addNewTag();
  };

  const handleDeleteTag = (tagToDelete: string) => {
    setTags(tags.filter((tag) => tag !== tagToDelete));
  };

  const handleExpertNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value.length > MAX_NOTES_LENGTH) {
      warningSnackbar(`Expert notes are limited to ${MAX_NOTES_LENGTH} characters.`);
      return;
    }
    setNotes(value);
  };

  const handleSave = () => {
    if (!priority) return;
    const enhancementData = { priority, tags, expertNote: notes };
    onSave(enhancementData);
    resetState();
    onClose();
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>Enhance insight</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box sx={{ flex: 1, mt: 1.5 }}>
          <TextField
            select
            fullWidth
            label="Priority"
            value={priority}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            onChange={(e) => {
              setPriority(e.target.value as Priority);
            }}
          >
            {PRIORITY_LIST.map((priorityOption) => (
              <MenuItem key={priorityOption.value} value={priorityOption.value}>
                {priorityOption.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <Box
            component="fieldset"
            p={0.8}
            sx={{
              flex: 1,
              color: "text.secondary",
              margin: 0,
              border: "1px solid",
              borderColor: "grey.400",
              borderRadius: 1,
              position: "relative",
            }}
          >
            <Typography
              component="legend"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 400,
                lineHeight: 1,
                width: "max-content",
                position: "absolute",
                top: -1.5,
                left: 10,
                transform: "translateY(-50%)",
                bgcolor: "background.paper",
              }}
            >
              Tags
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
              {tags.map((tag, index) => (
                <Chip
                  key={`${tag}-${index}`}
                  label={tag}
                  onDelete={() => {
                    handleDeleteTag(tag);
                  }}
                />
              ))}
              <TextField
                variant="standard"
                value={tagInput}
                onChange={handleTagInputChange}
                sx={{
                  ml: 0.5,
                  mb: -0.5,
                  flexGrow: 1,
                  width: "auto",
                }}
                slotProps={{
                  input: {
                    disableUnderline: true,
                  },
                }}
                onKeyDown={handleTagInputKeyDown}
                onBlur={handleTagInputBlur}
              />
            </Box>
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Each tag must have a maximum length of {MAX_TAG_LENGTH} characters.
          </Typography>
        </Box>
        <Box sx={{ flex: 2 }}>
          <TextField
            multiline
            rows={9}
            fullWidth
            label="Expert notes"
            value={notes}
            onChange={handleExpertNoteChange}
            variant="outlined"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
              input: {
                sx: { pt: 2, pb: 2 },
              },
            }}
          />
        </Box>
      </DialogContent>

      <Box border={1} borderColor="grey.300">
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              !priority ||
              (priority === (existingPriority ?? "") &&
                tags.length === (existingTags ?? []).length &&
                notes === (existingExpertNote ?? ""))
            }
          >
            Save changes
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default EnhanceInsightModal;
